import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { AuthResponse, User } from '@jungles/shared';
import { ApiError, api, loadToken, setToken } from '../lib/api';
import { guessTimezone, isTelegram, syncTelegramTheme, tg } from '../lib/telegram';

interface SessionValue {
  user: User | null;
  status: 'loading' | 'anonymous' | 'authorized';
  /** Вход через Telegram: initData уходит на сервер и проверяется подписью. */
  loginWithTelegram: () => Promise<void>;
  /** Локальный вход для разработки вне Telegram. */
  loginAsDev: (name: string) => Promise<void>;
  logout: () => void;
  updateUser: (next: User) => void;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<SessionValue['status']>('loading');

  const applyTheme = useCallback((next: User | null) => {
    const theme = next?.theme ?? 'dark';
    document.documentElement.dataset.theme = theme;
    syncTelegramTheme(theme);
  }, []);

  const adopt = useCallback(
    (response: AuthResponse) => {
      setToken(response.token);
      setUser(response.user);
      setStatus('authorized');
      applyTheme(response.user);
    },
    [applyTheme],
  );

  const loginWithTelegram = useCallback(async () => {
    const initData = tg()?.initData;
    if (!initData) throw new Error('Telegram недоступен');
    const response = await api.post<AuthResponse>('/api/auth/telegram', {
      initData,
      timezone: guessTimezone(),
    });
    adopt(response);
  }, [adopt]);

  const loginAsDev = useCallback(
    async (name: string) => {
      const response = await api.post<AuthResponse>('/api/auth/telegram', {
        dev: { name },
        timezone: guessTimezone(),
      });
      adopt(response);
    },
    [adopt],
  );

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setStatus('anonymous');
  }, []);

  const refresh = useCallback(async () => {
    try {
      const me = await api.get<User>('/api/me');
      setUser(me);
      setStatus('authorized');
      applyTheme(me);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) logout();
      else throw error;
    }
  }, [applyTheme, logout]);

  const updateUser = useCallback(
    (next: User) => {
      setUser(next);
      applyTheme(next);
    },
    [applyTheme],
  );

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      if (loadToken()) {
        try {
          const me = await api.get<User>('/api/me');
          if (cancelled) return;
          setUser(me);
          setStatus('authorized');
          applyTheme(me);
          return;
        } catch {
          setToken(null);
        }
      }

      // В Telegram вход происходит сам: подпись уже есть, спрашивать нечего.
      if (isTelegram()) {
        try {
          await loginWithTelegram();
          return;
        } catch {
          // Покажем экран входа с кнопкой и понятной ошибкой.
        }
      }

      if (!cancelled) {
        setStatus('anonymous');
        applyTheme(null);
      }
    };

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [applyTheme, loginWithTelegram]);

  const value = useMemo<SessionValue>(
    () => ({ user, status, loginWithTelegram, loginAsDev, logout, updateUser, refresh }),
    [user, status, loginWithTelegram, loginAsDev, logout, updateUser, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession вне SessionProvider');
  return value;
}

/** Внутри защищённых экранов пользователь гарантированно есть. */
export function useUser(): User {
  const { user } = useSession();
  if (!user) throw new Error('Нет пользователя в защищённом экране');
  return user;
}
